import type { ChannelMemberAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'
import { EDITABLE_POST_TYPES } from '@/client/services/worlds/constants'

import ChannelQ from './ChannelQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import PostQ from './PostQ'
import UserQ from './UserQ'

const ChannelMemberQ = {
  ...makeQueryModelV2<ChannelMemberAttributes>('channel_member')([
    'id',
    'user_id',
    'channel_id',
    'enable_text_notifications',
    'last_viewed_at',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Channel: {
      query: ChannelQ,
      foreignKey: ChannelQ.id,
      sourceKey: ChannelMemberQ.channel_id,
    },
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: ChannelMemberQ.user_id,
    },
  }),

  where: (
    {
      userId = null,
      channelId = null,
      enableTextNotifications = null,
      include = [],
      groupBy = null,
      order = null,
      paranoid = true,
    }: {
      userId?: number | null | SQLFragment<number>
      channelId?: number | null | SQLFragment<number>
      enableTextNotifications?: boolean | null | SQLFragment<boolean>
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<ChannelMemberAttributes> => SQL`
        FROM ${ChannelMemberQ}
        ${includeAssociationsToSql(ChannelMemberQ, include)}
        WHERE
            ${userId != null ? SQL`${ChannelMemberQ.user_id} = ${userId} AND` : SQL``}
            ${channelId != null ? SQL`${ChannelMemberQ.channel_id} = ${channelId} AND` : SQL``}
            ${
              enableTextNotifications != null
                ? SQL`${ChannelMemberQ.enable_text_notifications} = ${enableTextNotifications} AND`
                : SQL``
            }
            ${paranoid ? SQL`${ChannelMemberQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
    `,

  countPosts: (): SQLFragment<number> =>
    SQL`COUNT(*) ${PostQ.where(
      {
        channelId: ChannelMemberQ.channel_id,
        userId: ChannelMemberQ.user_id,
      },
      SQL`${PostQ.type} = ANY(${EDITABLE_POST_TYPES})`
    )}`,
}

export default ChannelMemberQ
